// XZ3D MOVEMENT core — occupancy, arc-length resampling, nav field, routing.
//
// The pure geometry behind obstacle-aware movement
// (plans/ft3d-pathing-design-2026-08-22.md). Sibling of ft3d-shape-core.js and
// held to the same discipline:
//
// Shared by the scene and browser-authoritative movement rules. Historical
// geometry vectors remain regression controls; PHP does not re-judge movement.
//
// Pure functions only: no THREE, no DOM, no clock, no randomness. Every
// tie-break is deterministic and every cost is an INTEGER, because two
// floating-point A* implementations WILL pick different equal-cost routes.
//
// AXES (the whole point of the migration): x and y are the horizontal FLOOR
// plane, z is elevation. This matches the shape blob and get_object_box()'s
// near/far depth extent. It is NOT the legacy Janus movement convention, where
// z stood in as the second axis of a side elevation.

import { insideUnion, segmentHit, ceilingAt, floorAt, sceneWallFaces, boxIsNavigationOpenGround } from './ft3d-shape-core.js';
import { squeezeBodyFt, squeezeTier } from './ft3d-creature-size.js';

const EPS = 1e-6;

// Cost scale. Orthogonal = 10, diagonal = 14 (≈10√2). Integers keep A*
// tie-breaking bit-identical across languages; floats do not.
export const COST_ORTHOGONAL = 10;
export const COST_DIAGONAL = 14;

// Difficult terrain and creature-occupied ground share ONE non-stacking cost
// class (owner D2): the multiplier is 2 or 1, never 4, and never 2 per extra
// creature. Mirrors difficult_terrain_step_multiplier(), which the engine
// already documents as non-stacking.
export const COST_MULT_NORMAL = 1;
export const COST_MULT_DIFFICULT = 2;

// Distinguished routing failures. Under owner D3 a blocked move is a real
// answer — but only ROUTE_NO_PATH actually means "nowhere to go". The rest mean
// "this field could not see the way", which is a WRONGLY blocked move if it is
// reported to the player as blocked. Callers must retry at finer resolution
// before believing them.
export const ROUTE_NO_PATH = 'no_path';
export const ROUTE_START_UNREACHABLE = 'start_unreachable';
export const ROUTE_END_UNREACHABLE = 'end_unreachable';
export const ROUTE_FIELD_TOO_COARSE = 'field_too_coarse';

// ---------------------------------------------------------------------------
// Occupancy shapes
// ---------------------------------------------------------------------------
// Owner D1/D1b: a creature may not END A MOVE inside another occupied zone, and
// the zone is a CYLINDER for ordinary actors — not the axis-aligned square of
// the 2D era. D8 carves out swarms: their size changes WIDTH only, so a swarm
// is an oriented OBLONG (dynamic width along facing, size-tier depth, size-tier
// height), the one occupancy shape that is not rotation-invariant.
//
// Both carry a vertical span so a flyer above a standee does not collide with
// it — that falls out for free rather than needing a flight special case.

export const OCCUPANCY_CIRCLE = 'circle';
export const OCCUPANCY_OBLONG = 'oblong';

export function circleOccupancy(x, y, radiusFt, zMin = 0, zMax = 0) {
  return {
    kind: OCCUPANCY_CIRCLE,
    x: +x,
    y: +y,
    r: Math.max(0, +radiusFt),
    zMin: +zMin,
    zMax: +zMax,
  };
}

export function oblongOccupancy(x, y, halfWidthFt, halfDepthFt, facingRad = 0, zMin = 0, zMax = 0) {
  return {
    kind: OCCUPANCY_OBLONG,
    x: +x,
    y: +y,
    halfW: Math.max(0, +halfWidthFt),
    halfD: Math.max(0, +halfDepthFt),
    facing: +facingRad,
    zMin: +zMin,
    zMax: +zMax,
  };
}

// Vertical spans overlap? Touching exactly (a.zMax === b.zMin) is NOT an
// overlap — a creature standing precisely on top of something is legal.
function verticalOverlap(a, b) {
  return a.zMin < b.zMax - EPS && b.zMin < a.zMax - EPS;
}

// World point -> an oblong's local frame (facing along +local-x).
function toLocal(shape, px, py) {
  const dx = px - shape.x;
  const dy = py - shape.y;
  const c = Math.cos(-shape.facing);
  const s = Math.sin(-shape.facing);
  return [dx * c - dy * s, dx * s + dy * c];
}

function circleCircleOverlap(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const minGap = a.r + b.r;
  return dx * dx + dy * dy < minGap * minGap - EPS;
}

function circleOblongOverlap(circle, oblong) {
  const [lx, ly] = toLocal(oblong, circle.x, circle.y);
  const cx = Math.max(-oblong.halfW, Math.min(oblong.halfW, lx));
  const cy = Math.max(-oblong.halfD, Math.min(oblong.halfD, ly));
  const dx = lx - cx;
  const dy = ly - cy;
  return dx * dx + dy * dy < circle.r * circle.r - EPS;
}

// Separating-axis test on the 4 face normals of two oriented rectangles.
function oblongOblongOverlap(a, b) {
  const axes = [a.facing, a.facing + Math.PI / 2, b.facing, b.facing + Math.PI / 2];
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  for (const angle of axes) {
    const ax = Math.cos(angle);
    const ay = Math.sin(angle);
    const project = (box) => {
      const ux = Math.cos(box.facing);
      const uy = Math.sin(box.facing);
      const vx = -uy;
      const vy = ux;
      return box.halfW * Math.abs(ux * ax + uy * ay)
        + box.halfD * Math.abs(vx * ax + vy * ay);
    };
    const centreGap = Math.abs(dx * ax + dy * ay);
    if (centreGap > project(a) + project(b) - EPS) return false;
  }
  return true;
}

/**
 * Do two occupancy zones overlap — on the floor plane AND vertically?
 * This is the whole of D1: end-of-move legality is one call.
 */
export function occupancyOverlap(a, b) {
  if (!a || !b) return false;
  if (!verticalOverlap(a, b)) return false;
  if (a.kind === OCCUPANCY_CIRCLE && b.kind === OCCUPANCY_CIRCLE) {
    return circleCircleOverlap(a, b);
  }
  if (a.kind === OCCUPANCY_CIRCLE) return circleOblongOverlap(a, b);
  if (b.kind === OCCUPANCY_CIRCLE) return circleOblongOverlap(b, a);
  return oblongOblongOverlap(a, b);
}

/**
 * The mover's own clearance radius — what A* inflates obstacles by. An oblong
 * uses its LARGER half-extent so clearance is never optimistic: a swarm that
 * fits sideways must not be routed through a gap it can only pass end-on.
 */
export function clearanceRadius(shape) {
  if (!shape) return 0;
  if (shape.kind === OCCUPANCY_CIRCLE) return shape.r;
  return Math.max(shape.halfW, shape.halfD);
}

/** Move an occupancy zone to a new floor-plane point, keeping its extents. */
export function occupancyAt(shape, x, y) {
  return { ...shape, x: +x, y: +y };
}

// ---------------------------------------------------------------------------
// Arc-length polyline resampler
// ---------------------------------------------------------------------------
/**
 * ⭐ THE FIX FOR THE PER-LEG CONCATENATION BUG.
 *
 * Feeding a routed polyline into the step loop by calling the old per-segment
 * generator once per leg and concatenating is WRONG two ways:
 *   1. every leg re-emits its own start, so each bend becomes a processed
 *      ZERO-DISTANCE step — which the walker charges for and which can classify
 *      a watcher as "stayed in reach" and fire target_moves for a step never
 *      taken;
 *   2. every leg rounds up independently (ceil per leg), so a many-bend route
 *      accrues nearly one extra charged foot PER BEND.
 *
 * This walks the WHOLE route by true arc length instead, carrying the
 * fractional residual across bends. Guarantees:
 *   - the start point is emitted exactly once;
 *   - interior samples are exactly `stepFt` apart BY ARC LENGTH, across bends;
 *   - the exact endpoint is always the final sample;
 *   - no two consecutive samples are ZERO ARC LENGTH apart;
 *   - the route has AT MOST ONE partial (sub-step) increment — the last one —
 *     however many bends it has. That single partial is charged as a full unit,
 *     which is exactly what the existing engine does for a straight move, so
 *     the cost model is unchanged rather than quietly re-tuned.
 *
 * ⚠ The invariant is arc length, NOT coordinate distinctness. A polyline that
 * retraces itself can legitimately emit the same coordinate twice a step apart —
 * the creature walked out and back. Routed polylines are simple paths by
 * construction (A* returns no repeated cell), so this only arises for
 * hand-supplied input.
 */
export function resamplePolyline(points, stepFt = 1) {
  const step = Math.max(EPS, +stepFt);
  const pts = [];
  for (const p of points || []) {
    // Reject the same input domain the PHP port rejects. `+null === 0` in JS
    // but PHP's isset() drops a null coordinate, so accepting it here would
    // silently make the two sides disagree on malformed input.
    if (!Array.isArray(p) || p[0] === null || p[0] === undefined
      || p[1] === null || p[1] === undefined) continue;
    const x = +p[0];
    const y = +p[1];
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    const last = pts[pts.length - 1];
    if (last && Math.abs(last[0] - x) < EPS && Math.abs(last[1] - y) < EPS) continue;
    pts.push([x, y]);
  }
  if (pts.length === 0) return [];
  if (pts.length === 1) return [[pts[0][0], pts[0][1]]];

  const out = [[pts[0][0], pts[0][1]]];
  let carry = 0; // arc length accumulated since the last emitted sample

  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    const segLen = Math.hypot(x1 - x0, y1 - y0);
    if (segLen < EPS) continue;
    const ux = (x1 - x0) / segLen;
    const uy = (y1 - y0) / segLen;

    let travelled = 0;
    while (carry + (segLen - travelled) >= step - EPS) {
      travelled += step - carry;
      carry = 0;
      out.push([x0 + ux * travelled, y0 + uy * travelled]);
    }
    carry += segLen - travelled;
  }

  // The endpoint is appended only when arc length actually remains. Deciding
  // this by COORDINATE equality instead was a bug: on a route that doubles back
  // (…[0,0] → [0.5,0] → [0,0]) the final sample legitimately lands on the start
  // coordinate after a full foot of travel, and a coordinate test read that as
  // "already there" and silently deleted a foot of real movement — along with
  // every trigger along it. Arc length is the truth; position is not.
  if (carry > EPS) {
    out.push([pts[pts.length - 1][0], pts[pts.length - 1][1]]);
  } else {
    // Snap the last whole-step sample onto the exact endpoint, killing float
    // drift without changing the step count.
    out[out.length - 1] = [pts[pts.length - 1][0], pts[pts.length - 1][1]];
  }
  return out;
}

/** True arc length of a polyline, in feet. The clamp and the cost both need it. */
export function polylineLength(points) {
  let total = 0;
  for (let i = 1; i < (points || []).length; i++) {
    total += Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]);
  }
  return total;
}

// ---------------------------------------------------------------------------
// Navigation field
// ---------------------------------------------------------------------------
// Descended from buildCellField() in ft3d-wfc-core.js, which its own source
// calls a placeholder for tile skinning — it has no mover footprint, no
// clearance and no headroom. This one adds all three, because a rules-grade
// graph has to answer "can THIS creature stand here", not "is this point in
// the union".
//
// ⚠ CLEARANCE CONTRACT — corrected 2026-08-22 after review. An earlier version
// of this comment claimed the probe test "can refuse a legal squeeze, it does
// not admit an illegal one." THAT WAS FALSE, and the false direction is the
// dangerous one, so it is stated plainly here:
//
//   A cell is walkable when the mover's clearance circle at the cell CENTRE is
//   unobstructed on N probe rays. This is SAMPLING, not proof. It errs BOTH
//   ways:
//     • false refusal  — a legal narrow passage whose cell centres fail a probe;
//     • false ADMISSION — a thin concave notch that enters the clearance disk
//       between two probe directions (worst case near the angular midpoint)
//       intersects the mover while every probe endpoint and spoke stays clear.
//
//   The wall-span distance test below now closes the sampled-spoke gaps for
//   structural faces, including the sides of narrow openings. Union membership
//   and continuous edge traversal still use the documented sampling checks.
//
// Under owner D3 (no route ⇒ blocked) a false refusal is a WRONGLY BLOCKED
// MOVE, so routing distinguishes its failure reasons (see ROUTE_* below) rather
// than returning one indistinguishable null.

const PROBE_COUNT = 16;
const PROBE_DIRS = Array.from({ length: PROBE_COUNT }, (_, i) => {
  const angle = (2 * Math.PI * i) / PROBE_COUNT;
  return [Math.cos(angle), Math.sin(angle)];
});

export function clearanceOk(shape, x, y, radius, rayZ) {
  if (radius <= EPS) return true;
  for (const [ux, uy] of PROBE_DIRS) {
    const tx = x + ux * Math.max(0, radius - EPS);
    const ty = y + uy * Math.max(0, radius - EPS);
    if (!insideUnion(shape, tx, ty)) return false;
    if (segmentHit(shape, x, y, tx, ty, { z: rayZ, mode: 'move' }) !== null) return false;
  }
  return wallPenetrationDepth(shape, x, y, radius, rayZ) <= EPS;
}

/** Exact-width corridors have one full-fit centreline. Small standee offsets do not change the gap's size tier.
 * Test the centred footprint with the same EPS and solid-face checks as passage admission. */
function corridorClearanceOk(shape, x, y, width, rayZ) {
  const faces = { x: [], y: [] };
  for (const box of shape.boxes) {
    if (boxIsNavigationOpenGround(box) || rayZ < box.floor || rayZ > box.ceiling) continue;
    if (y >= box.y.min && y <= box.y.max) faces.x.push(box.x.min, box.x.max);
    if (x >= box.x.min && x <= box.x.max) faces.y.push(box.y.min, box.y.max);
  }
  for (const wall of shape.sceneWalls ?? []) {
    if (!wall || rayZ < wall.z.min || rayZ > wall.z.max) continue;
    const along = wall.axis === 'x' ? y : x;
    if (along < wall.span.min || along > wall.span.max || (wall.openings ?? []).some(o => along >= o.span.min && along <= o.span.max
      && rayZ >= (o.z?.min ?? -Infinity) && rayZ <= (o.z?.max ?? Infinity))) continue;
    const half = Math.max(.05, wall.thicknessFt) / 2;
    faces[wall.axis].push(wall.at - half, wall.at + half);
  }
  const centres = (axis, value) => {
    let lo = -Infinity, hi = Infinity;
    for (const face of faces[axis]) {
      if (face < value) lo = Math.max(lo, face);
      if (face > value) hi = Math.min(hi, face);
    }
    return Math.abs(hi - lo - width) <= EPS ? [value, (lo + hi) / 2] : [value];
  };
  for (const cx of centres('x', x)) for (const cy of centres('y', y)) {
    if (cx === x && cy === y) continue;
    if (segmentHit(shape, x, y, cx, cy, { z: rayZ, mode: 'move' }) === null && clearanceOk(shape, cx, cy, width / 2, rayZ)) return true;
  }
  return false;
}

/** ADMISSION of a stop on the SizeType ladder: a body no more than two sizes smaller fits where it stands
 * (an exact-width corridor is judged on its centreline). The stop's Squeezed tier is clearanceSqueezeLevel's. */
export function squeezeFitOk(shape, x, y, radius, rayZ, squeezeSizeFt = radius * 2) {
  for (const level of [0, 1, 2]) {
    const width = squeezeBodyFt(squeezeSizeFt, level);
    if (clearanceOk(shape, x, y, width / 2, rayZ) || corridorClearanceOk(shape, x, y, width, rayZ)) return true;
  }
  return false;
}

/** `span` minus the stretches in `cuts`: the solid pieces of one wall face. */
function solidPieces(span, cuts) {
  let pieces = [span];
  for (const [lo, hi] of cuts) {
    pieces = pieces.flatMap(([a, b]) => hi <= a + EPS || lo >= b - EPS ? [[a, b]]
      : [[a, Math.min(b, lo)], [Math.max(a, hi), b]].filter(([p, q]) => q - p > EPS));
  }
  return pieces;
}

/** Every wall face at the ray height with its solid pieces, under segmentHit's movement rules: a passable opening at
 * this height and a stretch strictly inside another structural box are not wall. Scene walls contribute both slab
 * faces and their end caps. {axis} is the face normal; pieces run along the other axis. */
function passageFaces(shape, rayZ) {
  const faces = [];
  const structural = shape.boxes.filter(box => !boxIsNavigationOpenGround(box) && rayZ >= box.floor - EPS && rayZ <= box.ceiling + EPS);
  for (const box of structural) for (const axis of ['x', 'y']) {
    const along = axis === 'x' ? 'y' : 'x';
    for (const at of [box[axis].min, box[axis].max]) {
      const cuts = [];
      for (const opening of shape.openings ?? []) {
        if (opening.passable === false || opening.plane.axis !== axis || Math.abs(opening.plane.at - at) > EPS
          || rayZ < opening.z.min - EPS || rayZ > opening.z.max + EPS) continue;
        cuts.push([opening.span.min, opening.span.max]);
      }
      for (const other of structural) {
        if (other !== box && at > other[axis].min + EPS && at < other[axis].max - EPS) cuts.push([other[along].min, other[along].max]);
      }
      const pieces = solidPieces([box[along].min, box[along].max], cuts);
      if (pieces.length) faces.push({ axis, at, pieces });
    }
  }
  for (const face of sceneWallFaces(shape)) {
    if (rayZ < face.floor - EPS || rayZ > face.ceiling + EPS) continue;
    const pieces = solidPieces([face.min, face.max],
      face.openings.filter(o => rayZ >= o.zmin - EPS && rayZ <= o.zmax + EPS).map(o => [o.min, o.max]));
    if (pieces.length) faces.push({ axis: face.axis, at: face.at, pieces });
  }
  return faces;
}

/**
 * Free width of the narrowest passage a body of `radius` occupies at (x, y): on each axis, the clearance between the
 * nearest OPPOSING solid stretches the body stands between, at the ray height; the smaller axis answers. Where the body
 * stopped inside that passage does not change it.
 *   • A face the body has not reached opposes it along the face normal when the body's free width would meet solid there.
 *   • A face the body already overlaps on a solid stretch presses on it along the normal (an open room's wall, a corner).
 *   • A wall the body crosses inside an opening bounds nothing along its normal; the solid stretches its chord meets
 *     there are the jambs it stands between, and they bound the OTHER axis (room-wall doorways and scene-wall doorways alike).
 *   • Two opposing stretches whose solid overlaps along the passage are exactly their distance apart; stretches that do NOT
 *     overlap (staggered corners, a jamb facing a wall's end) oppose corner to corner, so a diagonal passage reads its true
 *     clearance and never the projection of its corners onto one axis (Astra r3 critic: sqrt(20) ft, not 2 ft).
 *   • A wall on one side alone, or an opening wider than the body's chord through it, is not a gap.
 * Infinity in the open; null when the centre itself is in a wall.
 */
export function passageGapWidth(shape, x, y, radius, rayZ) {
  // The opposing solid stretches on each side of each axis: `at` along the axis, [lo, hi] along the other (a jamb is its corner).
  const below = { x: [], y: [] }, above = { x: [], y: [] };
  const bound = (axis, n, at, lo, hi) => {
    if (at < n - EPS) below[axis].push({ at, lo, hi });
    else if (at > n + EPS) above[axis].push({ at, lo, hi });
  };
  const faces = passageFaces(shape, rayZ).map(face => {
    const n = face.axis === 'x' ? x : y, t = face.axis === 'x' ? y : x;
    return { ...face, n, t, d: Math.abs(n - face.at), pressed: face.pieces.find(([lo, hi]) => t > lo - EPS && t < hi + EPS) ?? null };
  });
  // Walls the body already overlaps on a solid stretch. The width still free of them is the body that can meet anything else.
  const free = { x: [x - radius, x + radius], y: [y - radius, y + radius] };
  for (const face of faces) {
    if (face.d >= radius - EPS || !face.pressed) continue;
    if (face.d <= EPS) return null;
    bound(face.axis, face.n, face.at, face.pressed[0], face.pressed[1]);
    if (face.at < face.n) free[face.axis][0] = Math.max(free[face.axis][0], face.at);
    else free[face.axis][1] = Math.min(free[face.axis][1], face.at);
  }
  for (const face of faces) {
    const other = face.axis === 'x' ? 'y' : 'x';
    if (face.d >= radius - EPS) {
      const [lo, hi] = free[other];
      for (const [a, b] of face.pieces) if (b > lo + EPS && a < hi - EPS) bound(face.axis, face.n, face.at, a, b);
      continue;
    }
    if (face.pressed) continue;
    const chord = Math.sqrt(Math.max(0, radius * radius - face.d * face.d));
    if (!face.pieces.some(([a, b]) => b > face.t - chord + EPS && a < face.t + chord - EPS)) continue;
    for (const [a, b] of face.pieces) {
      if (b <= face.t + EPS) below[other].push({ at: b, lo: face.at, hi: face.at });
      else if (a >= face.t - EPS) above[other].push({ at: a, lo: face.at, hi: face.at });
    }
  }
  // The nearest pair of opposing stretches on each axis; a pair whose solid does not overlap along the passage is corner to corner.
  const width = axis => {
    let best = Infinity;
    for (const near of below[axis]) for (const far of above[axis]) {
      const apart = Math.max(0, near.lo - far.hi, far.lo - near.hi);
      best = Math.min(best, Math.hypot(far.at - near.at, apart));
    }
    return best;
  };
  return Math.min(width('x'), width('y'));
}

/** The Squeezed TIER of a stop: squeezeTier(size, free width of the narrowest passage the body occupies).
 * 0 normal, 1 Squeezed, 2 Very Squeezed; null when no body down to two sizes smaller fits that gap, the centre is in a
 * wall, or the point is outside the union (a GM drop there keeps both canonical statuses). */
export function clearanceSqueezeLevel(shape, x, y, radius, rayZ, squeezeSizeFt = radius * 2) {
  if (!insideUnion(shape, x, y)) return null;
  const gap = passageGapWidth(shape, x, y, radius, rayZ);
  if (gap === null) return null;
  if (!Number.isFinite(gap)) return 0;
  const tier = squeezeTier(squeezeSizeFt, gap);
  return tier === 'normal' ? 0 : tier === 'squeezed' ? 1 : tier === 'very_squeezed' ? 2 : null;
}

/** Maximum body overlap with a solid wall span. An origin keeps penetration signed across its wall face. */
export function wallPenetrationDepth(shape, x, y, radius, rayZ, origin = null) {
  if (radius <= EPS) return 0;
  let depth = 0;
  // Exact circle distance to solid wall spans closes the gaps between sampled spokes.
  for (const box of shape.boxes) {
    if (boxIsNavigationOpenGround(box) || rayZ < box.floor || rayZ > box.ceiling) continue;
    for (const axis of ['x','y']) for (const at of [box[axis].min,box[axis].max]) {
      const normal = axis === 'x' ? x : y, along = axis === 'x' ? y : x;
      if (Math.abs(normal-at) >= radius-EPS) continue;
      const span = box[axis === 'x' ? 'y' : 'x'];
      let pieces = [[span.min,span.max]];
      for (const opening of shape.openings ?? []) {
        if (!opening.passable || opening.plane.axis !== axis || Math.abs(opening.plane.at-at)>EPS
          || rayZ < opening.z.min || rayZ > opening.z.max) continue;
        pieces = pieces.flatMap(([lo,hi]) => opening.span.max <= lo || opening.span.min >= hi ? [[lo,hi]]
          : [[lo,Math.min(hi,opening.span.min)],[Math.max(lo,opening.span.max),hi]].filter(([a,b])=>b-a>EPS));
      }
      for (const [lo,hi] of pieces) {
        const nearest = Math.max(lo,Math.min(hi,along));
        if (Math.hypot(normal-at,along-nearest) >= radius-EPS) continue;
        const middle = Math.max(lo+EPS*10,Math.min(hi-EPS*10,nearest));
        const hit = axis === 'x' ? segmentHit(shape,at-.01,middle,at+.01,middle,{z:rayZ,mode:'move'})
          : segmentHit(shape,middle,at-.01,middle,at+.01,{z:rayZ,mode:'move'});
        const originNormal = origin ? (axis === 'x' ? origin.x : origin.y) - at : 0;
        const crossed = Math.abs(originNormal) > EPS && originNormal * (normal-at) < 0 && along >= lo && along <= hi;
        if (hit !== null) depth = Math.max(depth, radius - (crossed ? -Math.abs(normal-at) : Math.hypot(normal-at,along-nearest)));
      }
    }
  }
  // DAFT3D S24 scene walls: the body circle may not overlap a wall slab (its doorways cut out).
  for (const wall of shape.sceneWalls ?? []) {
    if (!wall || rayZ < wall.z.min || rayZ > wall.z.max) continue;
    const normal = wall.axis === 'x' ? x : y, along = wall.axis === 'x' ? y : x;
    const gap = Math.max(0, Math.abs(normal - wall.at) - Math.max(.05, wall.thicknessFt) / 2);
    if (gap >= radius - EPS) continue;
    let pieces = [[wall.span.min, wall.span.max]];
    for (const opening of wall.openings ?? []) {
      if (rayZ < (opening.z?.min ?? -Infinity) || rayZ > (opening.z?.max ?? Infinity)) continue;
      pieces = pieces.flatMap(([lo, hi]) => opening.span.max <= lo || opening.span.min >= hi ? [[lo, hi]]
        : [[lo, Math.min(hi, opening.span.min)], [Math.max(lo, opening.span.max), hi]].filter(([a, b]) => b - a > EPS));
    }
    for (const [lo, hi] of pieces) {
      const normalGap = Math.abs(normal - wall.at) - Math.max(.05, wall.thicknessFt) / 2;
      const alongGap = Math.max(lo - along, along - hi);
      let distance = Math.hypot(Math.max(0, normalGap), Math.max(0, alongGap)) + Math.min(0, Math.max(normalGap, alongGap));
      const originNormal = origin ? (wall.axis === 'x' ? origin.x : origin.y) - wall.at : 0;
      if (Math.abs(originNormal) > EPS && originNormal * (normal-wall.at) < 0 && along >= lo && along <= hi)
        distance = -Math.abs(normal-wall.at) - Math.max(.05, wall.thicknessFt) / 2;
      if (distance < radius - EPS) depth = Math.max(depth, radius - distance);
    }
  }
  return depth;
}

/** Vertical room for a mover of `heightFt` at a floor-plane point. */
function headroomOk(shape, x, y, heightFt) {
  if (heightFt <= EPS) return true;
  const ceiling = ceilingAt(shape, x, y);
  const floor = floorAt(shape, x, y);
  if (ceiling === null || floor === null) return false;
  return ceiling - floor >= heightFt - EPS;
}

/**
 * Build the navigation field for ONE mover in ONE shape.
 *
 * Keyed by (shape, cellFt, clearanceFt, rayZ) — all four belong in any cache
 * key, along with an obstacle revision, because a swarm resizing on damage
 * (D8) changes occupancy with no movement at all.
 */
export function buildNavField(shape, {
  cellFt = 5,
  clearanceFt = 0,
  rayZ = 0.5,
  heightFt = 0,
  passableAt = null,
  passableStepFt = 1,
  gridAnchor = null,
} = {}) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const box of shape.boxes) {
    minX = Math.min(minX, box.x.min);
    minY = Math.min(minY, box.y.min);
    maxX = Math.max(maxX, box.x.max);
    maxY = Math.max(maxY, box.y.max);
  }
  const offsetX = gridAnchor ? gridAnchor[0]-cellFt/2 : 0;
  const offsetY = gridAnchor ? gridAnchor[1]-cellFt/2 : 0;
  const originX = Math.floor((minX-offsetX) / cellFt) * cellFt + offsetX;
  const originY = Math.floor((minY-offsetY) / cellFt) * cellFt + offsetY;
  const cols = Math.ceil((maxX - originX) / cellFt);
  const rows = Math.ceil((maxY - originY) / cellFt);

  const cells = new Map();
  for (let iy = 0; iy < rows; iy++) {
    for (let ix = 0; ix < cols; ix++) {
      const cx = originX + (ix + 0.5) * cellFt;
      const cy = originY + (iy + 0.5) * cellFt;
      if (!insideUnion(shape, cx, cy)) continue;
      if (!clearanceOk(shape, cx, cy, clearanceFt, rayZ)) continue;
      // Dynamic hard blockers (inventory/props) are caller-owned because they
      // are not part of the authored room shape. Creature space deliberately
      // does NOT use this lane: it remains traversable at x2 cost.
      if (passableAt && !passableAt(cx, cy)) continue;
      // Headroom belongs in the FIELD, not only at the destination: without it
      // a 5 ft creature happily routes through a 3 ft crawlway and is refused
      // only on arrival.
      if (!headroomOk(shape, cx, cy, heightFt)) continue;
      cells.set(`${ix},${iy}`, { key: `${ix},${iy}`, ix, iy, cx, cy });
    }
  }
  return {
    origin: { x: originX, y: originY },
    cellFt,
    clearanceFt,
    rayZ,
    heightFt,
    passableAt,
    passableStepFt,
    cols,
    rows,
    cells,
  };
}

/** The field cell containing a floor-plane point, or null. */
export function cellAt(field, x, y) {
  const ix = Math.floor((x - field.origin.x) / field.cellFt);
  const iy = Math.floor((y - field.origin.y) / field.cellFt);
  return field.cells.get(`${ix},${iy}`) ?? null;
}

/**
 * The nearest field cell a point OFF the field can reach by a swept-corridor connector, or null.
 * For an in-union point whose own cell was culled (a mover flush against a wall: its cell centre
 * cannot clear the wall by the full radius). Searches the cells within two cell widths plus the
 * clearance, nearest first by SQUARED distance, ties by row then column — exact in both languages,
 * so the client and Janus pick the same cell. Twin of ft_move_nearest_reachable_cell().
 */
function nearestReachableCell(shape, field, x, y, passableAt = field.passableAt ?? null) {
  if (!insideUnion(shape, x, y)) return null;
  const { cellFt } = field;
  const reach = 2 * cellFt + field.clearanceFt;
  const ixMin = Math.floor((x - reach - field.origin.x) / cellFt);
  const ixMax = Math.floor((x + reach - field.origin.x) / cellFt);
  const iyMin = Math.floor((y - reach - field.origin.y) / cellFt);
  const iyMax = Math.floor((y + reach - field.origin.y) / cellFt);
  const candidates = [];
  for (let iy = iyMin; iy <= iyMax; iy++) {
    for (let ix = ixMin; ix <= ixMax; ix++) {
      const cell = field.cells.get(`${ix},${iy}`);
      if (!cell) continue;
      const dx = cell.cx - x;
      const dy = cell.cy - y;
      candidates.push({ d2: dx * dx + dy * dy, cell });
    }
  }
  candidates.sort((a, b) => (a.d2 - b.d2) || (a.cell.iy - b.cell.iy) || (a.cell.ix - b.cell.ix));
  for (const { cell } of candidates) {
    if (edgePassable(shape, field, { cx: x, cy: y }, cell, passableAt)) return cell;
  }
  return null;
}

// Can the mover traverse between two adjacent cell centres? Tests the swept
// corridor, not a bare centre ray: the centre line plus both offset rails at
// ±clearance perpendicular, so a gap narrower than the mover is refused.
function edgePassable(shape, field, from, to, passableAt = field.passableAt ?? null) {
  const { rayZ, clearanceFt, heightFt = 0 } = field;
  if (segmentHit(shape, from.cx, from.cy, to.cx, to.cy, { z: rayZ, mode: 'move' }) !== null) {
    return false;
  }
  // Headroom along the edge, sampled per foot — a ceiling can dip between two
  // cell centres that both clear it.
  if (heightFt > EPS) {
    const span = Math.hypot(to.cx - from.cx, to.cy - from.cy);
    const steps = Math.max(1, Math.ceil(span));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      if (!headroomOk(shape, from.cx + (to.cx - from.cx) * t, from.cy + (to.cy - from.cy) * t, heightFt)) {
        return false;
      }
    }
  }
  if (passableAt) {
    const span = Math.hypot(to.cx - from.cx, to.cy - from.cy);
    const steps = Math.max(1, Math.ceil(span / Math.max(.05, field.passableStepFt ?? 1)));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      if (!passableAt(from.cx + (to.cx - from.cx) * t, from.cy + (to.cy - from.cy) * t)) {
        return false;
      }
    }
  }
  if (clearanceFt <= EPS) return true;
  const dx = to.cx - from.cx;
  const dy = to.cy - from.cy;
  const len = Math.hypot(dx, dy);
  if (len < EPS) return true;
  // Parallel rails can lie inside a thick wall without crossing its centre plane.
  // Check the clearance disc too; an overlapping origin may only leave its wall, never enter more deeply.
  let penetration = wallPenetrationDepth(shape, from.cx, from.cy, clearanceFt, rayZ);
  const steps = Math.max(1, Math.ceil(len));
  for (let i = 1; i <= steps; i++) {
    const x = from.cx + dx * i / steps, y = from.cy + dy * i / steps;
    if (clearanceOk(shape, x, y, clearanceFt, rayZ)) { penetration = 0; continue; }
    const depth = wallPenetrationDepth(shape, x, y, clearanceFt, rayZ);
    if (!(depth > EPS && depth < penetration - EPS)) return false;
    penetration = depth;
  }
  const px = (-dy / len) * Math.max(0, clearanceFt - EPS);
  const py = (dx / len) * Math.max(0, clearanceFt - EPS);
  for (const sign of [1, -1]) {
    const ax = from.cx + px * sign;
    const ay = from.cy + py * sign;
    const bx = to.cx + px * sign;
    const by = to.cy + py * sign;
    if (!insideUnion(shape, ax, ay) || !insideUnion(shape, bx, by)) return false;
    if (segmentHit(shape, ax, ay, bx, by, { z: rayZ, mode: 'move' }) !== null) return false;
  }
  return true;
}

/**
 * The cost multiplier over a whole SEGMENT, sampled per foot and taken as the
 * maximum. Sampling only the destination cell centre (the first version) let an
 * edge slice straight through a creature's cylinder at cost 1 whenever neither
 * cell centre happened to fall inside it.
 *
 * Non-stacking (owner D2): the result is 1 or 2, never a product.
 */
function segmentCostMultiplier(costMultiplierAt, ax, ay, bx, by, maximum = COST_MULT_DIFFICULT) {
  if (!costMultiplierAt) return COST_MULT_NORMAL;
  const span = Math.hypot(bx - ax, by - ay);
  const steps = Math.max(1, Math.ceil(span));
  let worst = COST_MULT_NORMAL;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const raw = Math.trunc(costMultiplierAt(ax + (bx - ax) * t, ay + (by - ay) * t));
    const mult = Math.max(COST_MULT_NORMAL, Math.min(maximum, raw));
    if (mult > worst) worst = mult;
    if (worst === maximum) break;
  }
  return worst;
}

// 8-neighbour, listed in a FIXED order so expansion is deterministic. Diagonals
// may not cut a corner: both orthogonal neighbours must also be open.
const NEIGHBOURS = [
  { dx: 0, dy: 1, cost: COST_ORTHOGONAL },
  { dx: 1, dy: 0, cost: COST_ORTHOGONAL },
  { dx: 0, dy: -1, cost: COST_ORTHOGONAL },
  { dx: -1, dy: 0, cost: COST_ORTHOGONAL },
  { dx: 1, dy: 1, cost: COST_DIAGONAL },
  { dx: 1, dy: -1, cost: COST_DIAGONAL },
  { dx: -1, dy: 1, cost: COST_DIAGONAL },
  { dx: -1, dy: -1, cost: COST_DIAGONAL },
];

// Octile distance in the same integer scale as the edge costs — admissible, so
// A* stays optimal.
function heuristic(a, b) {
  const dx = Math.abs(a.ix - b.ix);
  const dy = Math.abs(a.iy - b.iy);
  return COST_ORTHOGONAL * (dx + dy) + (COST_DIAGONAL - 2 * COST_ORTHOGONAL) * Math.min(dx, dy);
}

/**
 * A* over the nav field. Returns { cells, cost } or null when no route exists
 * within `maxExpansions`.
 *
 * ⛔ EXHAUSTION FAILS CLOSED. Under owner D3 "no legal route ⇒ blocked", so a
 * capped-out search returns null and the caller refuses the move. It must NEVER
 * fall back to a straight line: that is geometrically invalid (it can cross a
 * wall) and is the original bug wearing a marker.
 *
 * `costMultiplierAt(x, y)` returns 1 or 2 and is where owner D2 lives —
 * difficult terrain and creature-occupied ground share ONE non-stacking class.
 */
export function findRoute(shape, field, fromCell, toCell, {
  costMultiplierAt = null,
  passableAt = field.passableAt ?? null,
  maxExpansions = 20000,
  nearestTo = null,
  canEndAt = null,
  maxCostMultiplier = COST_MULT_DIFFICULT,
} = {}) {
  if (!fromCell || !toCell) return null;
  if (!nearestTo && fromCell.key === toCell.key) return { cells: [fromCell], cost: 0 };
  let nearestKey = null;
  let nearestDistance = Infinity;

  const gScore = new Map([[fromCell.key, 0]]);
  const cameFrom = new Map();
  const closed = new Set();
  // Deliberately a linear-scan frontier rather than a binary heap: heap sift
  // order is an implementation detail that would have to match byte-for-byte
  // across two languages. Scanning with an explicit, total tie-break does not.
  const open = new Map([[fromCell.key, heuristic(fromCell, toCell)]]);

  let expansions = 0;
  while (open.size > 0) {
    if (++expansions > maxExpansions) return null;

    let currentKey = null;
    let bestF = Infinity;
    let bestH = Infinity;
    for (const [key, f] of open) {
      const cell = field.cells.get(key);
      const h = heuristic(cell, toCell);
      // Total, deterministic ordering: f, then h, then the cell key string.
      // Without the final term two languages can pick different equal-cost
      // routes — the exact divergence the parity contract exists to prevent.
      if (f < bestF - EPS
        || (Math.abs(f - bestF) < EPS && h < bestH - EPS)
        || (Math.abs(f - bestF) < EPS && Math.abs(h - bestH) < EPS && (currentKey === null || key < currentKey))) {
        currentKey = key;
        bestF = f;
        bestH = h;
      }
    }
    if (currentKey === null) return null;

    if (!nearestTo && currentKey === toCell.key) {
      const cells = [];
      let walk = currentKey;
      while (walk !== undefined) {
        cells.unshift(field.cells.get(walk));
        walk = cameFrom.get(walk);
      }
      return { cells, cost: gScore.get(currentKey) };
    }

    open.delete(currentKey);
    closed.add(currentKey);
    const current = field.cells.get(currentKey);
    // Exhaust the reachable component for the closest legal endpoint. A capped
    // search still fails closed above; it never claims an unproven best point.
    if (nearestTo && (!canEndAt || canEndAt(current.cx, current.cy))) {
      const distance = Math.hypot(current.cx - nearestTo[0], current.cy - nearestTo[1]);
      if (distance < nearestDistance - EPS
        || (Math.abs(distance - nearestDistance) < EPS && (nearestKey === null
          || gScore.get(currentKey) < gScore.get(nearestKey)
          || (gScore.get(currentKey) === gScore.get(nearestKey) && currentKey < nearestKey)))) {
        nearestDistance = distance;
        nearestKey = currentKey;
      }
    }

    for (const step of NEIGHBOURS) {
      const neighbour = field.cells.get(`${current.ix + step.dx},${current.iy + step.dy}`);
      if (!neighbour || closed.has(neighbour.key)) continue;
      if (step.dx !== 0 && step.dy !== 0) {
        // No corner cutting.
        if (!field.cells.get(`${current.ix + step.dx},${current.iy}`)) continue;
        if (!field.cells.get(`${current.ix},${current.iy + step.dy}`)) continue;
      }
      if (!edgePassable(shape, field, current, neighbour, passableAt)) continue;

      const mult = segmentCostMultiplier(
        costMultiplierAt, current.cx, current.cy, neighbour.cx, neighbour.cy, maxCostMultiplier);
      const tentative = gScore.get(currentKey) + step.cost * mult;
      if (gScore.has(neighbour.key) && tentative >= gScore.get(neighbour.key) - EPS) continue;

      cameFrom.set(neighbour.key, currentKey);
      gScore.set(neighbour.key, tentative);
      open.set(neighbour.key, tentative + heuristic(neighbour, toCell));
    }
  }
  if (nearestKey !== null) {
    const cells = [];
    let walk = nearestKey;
    while (walk !== undefined) { cells.unshift(field.cells.get(walk)); walk = cameFrom.get(walk); }
    return { cells, cost: gScore.get(nearestKey) };
  }
  return null;
}

/**
 * String-pull a cell route into a smooth polyline: greedily drop any waypoint
 * whose skip-segment is still traversable for the mover. Endpoints are the real
 * start/end points, not cell centres, so the route begins and ends where the
 * creature actually is.
 *
 * ⚠ Uses the same swept-corridor test as edgePassable, NOT a bare centre ray —
 * string-pulling on a centre line is exactly how a large creature's footprint
 * gets cut through a corner.
 */
export function stringPull(shape, field, cells, start, end, {
  costMultiplierAt = null,
  passableAt = field.passableAt ?? null,
  maxCostMultiplier = COST_MULT_DIFFICULT,
} = {}) {
  const points = [[start[0], start[1]]];
  for (const cell of cells.slice(1, -1)) points.push([cell.cx, cell.cy]);
  points.push([end[0], end[1]]);

  const clear = (a, b) => edgePassable(
    shape,
    field,
    { cx: a[0], cy: a[1] },
    { cx: b[0], cy: b[1] },
    passableAt,
  );

  const out = [points[0]];
  let anchor = 0;
  while (anchor < points.length - 1) {
    let furthest = -1;
    for (let probe = points.length - 1; probe > anchor; probe--) {
      if (!clear(points[anchor], points[probe])) continue;
      // ⛔ COST-PRESERVING (owner D2). Skipping waypoints is a geometric
      // smoothing, and it must never smooth a route back through the weighted
      // ground A* deliberately routed around. A shortcut is only taken when it
      // costs no more than the leg it replaces.
      if (costMultiplierAt) {
        const shortcut = segmentCostMultiplier(
          costMultiplierAt, points[anchor][0], points[anchor][1], points[probe][0], points[probe][1], maxCostMultiplier);
        let original = COST_MULT_NORMAL;
        for (let k = anchor; k < probe; k++) {
          const leg = segmentCostMultiplier(
            costMultiplierAt, points[k][0], points[k][1], points[k + 1][0], points[k + 1][1], maxCostMultiplier);
          if (leg > original) original = leg;
        }
        if (shortcut > original) continue;
      }
      furthest = probe;
      break;
    }
    // ⛔ FAIL CLOSED. The previous version defaulted to anchor+1 and appended
    // that point even when it had just PROVED the leg blocked — so a route could
    // be returned containing a segment that crosses a wall, which is the exact
    // thing this whole system exists to prevent.
    if (furthest === -1) return null;
    out.push(points[furthest]);
    anchor = furthest;
  }

  // Belt and braces: validate every leg of the finished polyline. A* only ever
  // checked cell-centre edges; the real start/end connectors were never among
  // them.
  for (let i = 1; i < out.length; i++) {
    if (!clear(out[i - 1], out[i])) return null;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Destination resolution — the successor to find_free_position()
// ---------------------------------------------------------------------------
// find_free_position() nudges in X only, 0.5 ft at a time, ±15 ft, and has no
// wall model at all — which is complete for a world with ONE horizontal axis
// and wrong the moment there are two. It also returns a bare null that callers
// read as "just allow the overlap", which is how a token ends up somewhere no
// walk could reach.
//
// This replaces both halves: a real planar resolution, and a TYPED result.

export const PLACEMENT_PLACED = 'placed';
export const PLACEMENT_BLOCKED = 'blocked';

/**
 * Is this an entirely legal place for the mover to END a move (owner D1)?
 * Returns null when legal, else a short machine-readable reason.
 *
 * `obstacles` are occupancy zones EXCLUDING the mover and every constituent of
 * its compound actor (owner D1c: a rider occupies its mount's space by
 * definition, so mount/rider/pilot/grapple groups must be filtered out by the
 * caller before they get here, or every mounted state reads as illegal).
 */
export function placementLegality(shape, mover, obstacles, { rayZ = 0.5, headroomFt = 0, allowSqueeze = false, squeezeSizeFt, ignoreClearance = false } = {}) {
  if (!insideUnion(shape, mover.x, mover.y)) return 'outside_union';

  const radius = clearanceRadius(mover);
  if (!ignoreClearance && (allowSqueeze ? !squeezeFitOk(shape, mover.x, mover.y, radius, rayZ, squeezeSizeFt)
    : !clearanceOk(shape, mover.x, mover.y, radius, rayZ))) return 'no_clearance';

  if (headroomFt > 0) {
    const ceiling = ceilingAt(shape, mover.x, mover.y);
    const floor = floorAt(shape, mover.x, mover.y);
    if (ceiling === null || floor === null) return 'no_headroom';
    if (ceiling - floor < headroomFt - EPS) return 'no_headroom';
  }

  for (const other of obstacles || []) {
    if (occupancyOverlap(mover, other)) return 'occupied';
  }
  return null;
}

/**
 * Resolve a proposed destination to the nearest LEGAL one, or report it blocked.
 *
 * Descended from resolveCollision() in demos/xz3d/campsite-lab.js, whose own
 * comment calls it the replacement for find_free_position's X-only nudge. Two
 * things are added here that the lab version does not need:
 *
 *   1. every candidate must be REACHABLE from the proposed point — a nudge that
 *      teleports a token through a wall is the bug, not the fix;
 *   2. the result is TYPED. `blocked` is a real answer that the caller must
 *      honour by refusing the move (owner D3), not a null to paper over by
 *      persisting the overlapping position anyway.
 *
 * Candidates are generated on the 4 axes of each blocking zone at exactly the
 * separation that clears it, then sorted nearest-first with a deterministic
 * tie-break, because two implementations that pick different equally-near
 * candidates produce a ghost that lies about where the drop lands.
 */
export function resolvePlacement(shape, mover, obstacles, {
  rayZ = 0.5,
  headroomFt = 0,
  maxPasses = 8,
  allowSqueeze = false,
  squeezeSizeFt,
  ignoreClearance = false,
} = {}) {
  let current = mover;
  const reason = placementLegality(shape, current, obstacles, { rayZ, headroomFt, allowSqueeze, squeezeSizeFt, ignoreClearance });
  if (reason === null) {
    return { status: PLACEMENT_PLACED, x: current.x, y: current.y, moved: false };
  }
  // Outside the union or under a ceiling is not something a sideways nudge can
  // fix — those are the caller's aim being wrong, not a crowding problem.
  if (reason === 'outside_union' || reason === 'no_headroom' || reason === 'no_clearance') {
    return { status: PLACEMENT_BLOCKED, reason, x: mover.x, y: mover.y };
  }

  const origin = { x: mover.x, y: mover.y };
  // Canonical obstacle order: which blocker is resolved first must not depend on
  // roster order, or two callers with the same board pick different destinations.
  const ordered = [...(obstacles || [])].sort((a, b) => {
    if (Math.abs(a.x - b.x) > EPS) return a.x - b.x;
    if (Math.abs(a.y - b.y) > EPS) return a.y - b.y;
    return clearanceRadius(a) - clearanceRadius(b);
  });

  for (let pass = 0; pass < maxPasses; pass++) {
    const blocker = ordered.find(other => occupancyOverlap(current, other));
    if (!blocker) break;

    const gap = clearanceRadius(current) + clearanceRadius(blocker);
    // Axis AND diagonal candidates. Axis-only was incomplete in a way that
    // reported a legal spot as wedged: a mover boxed in on all four axes can
    // still have clear ground on the diagonal, and the earlier version never
    // looked there. This is BOUNDED, not complete — a full planar search would
    // also consider boundary intersections of overlapping zones.
    const diag = gap / Math.SQRT2;
    const candidates = [
      { x: blocker.x + gap, y: current.y },
      { x: blocker.x - gap, y: current.y },
      { x: current.x, y: blocker.y + gap },
      { x: current.x, y: blocker.y - gap },
      { x: blocker.x + diag, y: blocker.y + diag },
      { x: blocker.x + diag, y: blocker.y - diag },
      { x: blocker.x - diag, y: blocker.y + diag },
      { x: blocker.x - diag, y: blocker.y - diag },
    ]
      .map(c => occupancyAt(current, c.x, c.y))
      .filter(c => placementLegality(shape, c, ordered, { rayZ, headroomFt, allowSqueeze, squeezeSizeFt, ignoreClearance }) === null)
      // Reachable from where we started, not just legal in isolation.
      .filter(c => segmentHit(shape, origin.x, origin.y, c.x, c.y, { z: rayZ, mode: 'move' }) === null)
      .sort((a, b) => {
        const da = Math.hypot(a.x - origin.x, a.y - origin.y);
        const db = Math.hypot(b.x - origin.x, b.y - origin.y);
        if (Math.abs(da - db) > EPS) return da - db;
        // Deterministic tie-break; equally-near candidates must not depend on
        // which language sorted them.
        if (Math.abs(a.x - b.x) > EPS) return a.x - b.x;
        return a.y - b.y;
      });

    if (candidates.length === 0) {
      return { status: PLACEMENT_BLOCKED, reason: 'wedged', x: mover.x, y: mover.y };
    }
    current = candidates[0];
  }

  const finalReason = placementLegality(shape, current, obstacles, { rayZ, headroomFt, allowSqueeze, squeezeSizeFt, ignoreClearance });
  if (finalReason !== null) {
    return { status: PLACEMENT_BLOCKED, reason: finalReason, x: mover.x, y: mover.y };
  }
  return {
    status: PLACEMENT_PLACED,
    x: current.x,
    y: current.y,
    moved: Math.hypot(current.x - origin.x, current.y - origin.y) > EPS,
  };
}

/**
 * The whole route in one call: A* → string-pull → arc-length resample.
 * Returns null when there is no legal route (D3: blocked, never straight-lined).
 */
/** Align a squeeze search to an actual opening so an exact-width passage has a cell on its centreline. */
export function openingGridAnchor(shape, from, to) {
  const anchors = (shape.openings ?? []).filter(o=>o.passable).map(o=>o.plane.axis==='x'
    ? [o.plane.at,(o.span.min+o.span.max)/2] : [(o.span.min+o.span.max)/2,o.plane.at]);
  anchors.sort((a,b)=>Math.hypot(a[0]-(from[0]+to[0])/2,a[1]-(from[1]+to[1])/2)
    -Math.hypot(b[0]-(from[0]+to[0])/2,b[1]-(from[1]+to[1])/2));
  return anchors[0] ?? null;
}

export function routeBetween(shape, from, to, {
  cellFt = 5,
  clearanceFt = 0,
  rayZ = 0.5,
  heightFt = 0,
  stepFt = 1,
  costMultiplierAt = null,
  passableAt = null,
  passableStepFt = 1,
  maxExpansions = 20000,
  gridAnchor = openingGridAnchor(shape, from, to),
  field = null,
  allowSqueeze = false,
  squeezeSizeFt = clearanceFt * 2,
  maxCostMultiplier = COST_MULT_DIFFICULT,
} = {}) {
  if (allowSqueeze && clearanceFt > 0) {
    // Passing a tight point carries no squeeze effect or surcharge. The committed endpoint owns the status.
    const minimumRadius = squeezeBodyFt(squeezeSizeFt, 2) / 2;
    const squeezeField = buildNavField(shape,{cellFt,clearanceFt:minimumRadius,rayZ,heightFt,passableAt,passableStepFt,gridAnchor:openingGridAnchor(shape,from,to)});
    return routeBetween(shape, from, to, { cellFt, clearanceFt: minimumRadius,
      rayZ, heightFt, stepFt, costMultiplierAt, passableAt, passableStepFt,
      maxExpansions, maxCostMultiplier, field:squeezeField });
  }
  const navField = field ?? buildNavField(shape, {
    cellFt,
    clearanceFt,
    rayZ,
    heightFt,
    passableAt,
    passableStepFt,
    gridAnchor,
  });
  const straightClear = insideUnion(shape, from[0], from[1])
    && insideUnion(shape, to[0], to[1])
    && edgePassable(
      shape,
      navField,
      { cx: from[0], cy: from[1] },
      { cx: to[0], cy: to[1] },
      passableAt,
    );
  const straightResult = () => {
    const polyline = [[from[0], from[1]], [to[0], to[1]]];
    return { route: { polyline, samples: resamplePolyline(polyline, stepFt),
      lengthFt: polylineLength(polyline), field: navField }, reason: null };
  };

  // ⛔ The straight shortcut is only valid when nothing is WEIGHTED. Taking it
  // unconditionally meant a x2 strip never influenced the route at all: the
  // direct line was returned before costMultiplierAt was ever consulted, so
  // owner D2 was decorative. Now the shortcut is taken only when the direct
  // line is genuinely unweighted — otherwise A* decides whether paying x2 beats
  // walking around.
  // It is tested BEFORE the endpoint cells (2026-09-10): a mover standing flush against a wall has
  // no nav cell of its own (a cell centre must clear the wall by the full radius), yet the swept
  // corridor straight away from that wall is proven clear — refusing it read as "No walkable route"
  // for every Large creature parked on a wall.
  if (straightClear) {
    const straightMult = segmentCostMultiplier(costMultiplierAt, from[0], from[1], to[0], to[1], maxCostMultiplier);
    if (straightMult === COST_MULT_NORMAL) {
      const polyline = [[from[0], from[1]], [to[0], to[1]]];
      return {
        route: {
          polyline,
          samples: resamplePolyline(polyline, stepFt),
          lengthFt: polylineLength(polyline),
          field: navField,
        },
        reason: null,
      };
    }
  }

  // An endpoint with no cell of its own (flush against a wall, or inside the clearance band of a
  // narrow room) joins the field through the nearest cell it can reach by a swept-corridor
  // connector. The string pull below starts and ends at the REAL points and re-validates that
  // connector like every other leg, so this can never admit a leg the field would refuse.
  const fromCell = cellAt(navField, from[0], from[1])
    ?? nearestReachableCell(shape, navField, from[0], from[1], passableAt);
  const toCell = cellAt(navField, to[0], to[1])
    ?? nearestReachableCell(shape, navField, to[0], to[1], passableAt);
  // Distinguished so callers can tell "genuinely nowhere to go" from "our grid
  // was too coarse to see the way" — under D3 the second is a wrongly blocked
  // move and should be retried finer, not reported to the player as blocked.
  // A weighted corridor is still traversable. Grid centres may all miss an exact-width
  // passage; use the proven swept corridor when the grid cannot supply a detour.
  if (!fromCell) return straightClear ? straightResult() : { route: null, reason: ROUTE_START_UNREACHABLE };
  if (!toCell) return straightClear ? straightResult() : { route: null, reason: ROUTE_END_UNREACHABLE };

  const route = findRoute(shape, navField, fromCell, toCell, {
    costMultiplierAt,
    passableAt,
    maxExpansions,
    maxCostMultiplier,
  });
  if (!route) return straightClear ? straightResult() : { route: null, reason: ROUTE_NO_PATH };

  const polyline = stringPull(shape, navField, route.cells, from, to, {
    costMultiplierAt,
    passableAt,
    maxCostMultiplier,
  });
  if (polyline === null) {
    // A* found a cell path but the real endpoint connectors are not traversable
    // — most often because start and end share one coarse cell with a wall
    // between them. That is a grid-resolution failure, not proof of no route.
    return straightClear
      ? {
        route: {
          polyline: [[from[0], from[1]], [to[0], to[1]]],
          samples: resamplePolyline([[from[0], from[1]], [to[0], to[1]]], stepFt),
          lengthFt: polylineLength([[from[0], from[1]], [to[0], to[1]]]),
          field: navField,
        },
        reason: null,
      }
      : { route: null, reason: ROUTE_FIELD_TOO_COARSE };
  }

  return {
    route: {
      polyline,
      samples: resamplePolyline(polyline, stepFt),
      lengthFt: polylineLength(polyline),
      field: navField,
    },
    reason: null,
  };
}
